import { useCallback, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";

/**
 * Sütun başlığından sıralama. İlk tık "en iyi" üstte (metin A→Z, sayı büyükten küçüğe,
 * lowBest sütunlarında küçükten büyüğe), ikinci tık tersi.
 */
export function useTableSort(rows, initial = null) {
  const [sort, setSort] = useState(initial);
  const cols = useRef({});

  const register = useCallback((key, get, lowBest, text) => {
    cols.current[key] = { get, lowBest, text };
  }, []);

  const toggle = useCallback((key) => {
    setSort((s) => {
      const c = cols.current[key] || {};
      const first = c.text || c.lowBest ? "asc" : "desc";
      if (!s || s.key !== key) return { key, dir: first };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }, []);

  const applySort = useCallback((list) => {
    if (!sort || !cols.current[sort.key]) return list || [];
    const { get } = cols.current[sort.key];
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...(list || [])].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      const na = va === null || va === undefined || va === "";
      const nb = vb === null || vb === undefined || vb === "";
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
      if (typeof va === "boolean" && typeof vb === "boolean") return ((va ? 1 : 0) - (vb ? 1 : 0)) * sign;
      return String(va).localeCompare(String(vb), "tr") * sign;
    });
  }, [sort]);

  const sorted = useMemo(() => applySort(rows), [rows, applySort]);

  const Th = useCallback(({ sortKey, get, lowBest = false, text = false, className = "", children, title, ...rest }) => {
    if (sortKey) register(sortKey, get, lowBest, text);
    const active = sort?.key === sortKey;
    const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    if (!sortKey) return <th className={className} title={title} {...rest}>{children}</th>;
    return (
      <th className={`${className} cursor-pointer select-none hover:text-foreground transition-colors`}
        onClick={() => toggle(sortKey)} title={title || "Sıralamak için tıklayın"}
        data-testid={`sort-${sortKey}`} {...rest}>
        <span className="inline-flex items-center gap-1">
          {children}
          <Icon className={`h-3 w-3 shrink-0 ${active ? "opacity-100" : "opacity-30"}`} />
        </span>
      </th>
    );
  }, [register, sort, toggle]);

  return { sorted, sort, setSort, Th, applySort };
}
